import React, { useEffect, useMemo, useRef, useState } from 'react';
import iconArrow from '../../assets/arrow-right.svg';
import iconScan  from '../../assets/scan.svg';
import { formatEUR } from './constants';
import ItemSelector from './ItemSelector';

/**
 * DiscountManager — panneau droit de l'étape 1.
 *
 * Reproduit exactement la logique de l'ancien DiscountModal :
 * - scope : 'cart' | 'line'
 * - type  : 'percent' | 'amount'
 * - value : number
 * - line_uuid : uuid de SalesLine (pour scope='line')
 *
 * onApplyDiscount({ scope, type, value, line_uuid }) → appelé tel quel,
 * branché sur applyReceiptDiscount(receiptUuid, payload) dans useCheckoutState.
 */
const QUICK_PERCENTS = [5, 10, 15, 20, 25, 30, 40, 50];

const DiscountManager = ({
    orderItems,          // articles locaux (pour le sélecteur visuel)
    receiptLines,        // lignes backend [{ uuid, designation, amount_including_tax }]
    onApplyDiscount,     // ({ scope, type, value, line_uuid }) => void
    disabled = false,
}) => {
    const [type,  setType]  = useState('percent'); // 'percent' | 'amount'
    const [value, setValue] = useState('');

    // Pour remise sur article — on passe par l'ItemSelector visuel
    // mais on résout le line_uuid depuis receiptLines
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);

    const inputRef = useRef(null);

    const numericValue = useMemo(() => {
        const v = parseFloat(String(value).replace(',', '.'));
        return Number.isFinite(v) ? v : NaN;
    }, [value]);

    const canApply = Number.isFinite(numericValue) && numericValue > 0;

    // ── Remise sur le panier total ────────────────────────────────────────────
    const handleApplyCart = () => {
        if (!canApply) return;
        onApplyDiscount({
            scope:     'cart',
            type,
            value:     numericValue,
            line_uuid: null,
        });
        setValue('');
    };

    // ── Remise sur un article ─────────────────────────────────────────────────
    // selections = [{ id, qty }] depuis ItemSelector
    // On résout le line_uuid depuis receiptLines par correspondance de nom
    const handleApplyItem = (selections) => {
        if (!canApply || !selections.length) return;

        for (const sel of selections) {
            const item     = orderItems.find(i => String(i.id) === String(sel.id));
            const itemName = String(item?.name ?? '').toLowerCase().trim();

            // Cherche la ligne backend par nom (designation)
            // SalesLine.serialize() retourne 'wording' (pas designation)
            const lineMatch = receiptLines.find(l =>
                String(l.wording ?? l.designation ?? l.label ?? l.name ?? '').toLowerCase().trim() === itemName
            );

            if (!lineMatch?.uuid) {
                console.warn('[DiscountManager] UUID de ligne introuvable pour :', item?.name,
                    '— lignes disponibles :', receiptLines.map(l => l.wording));
                continue;
            }

            onApplyDiscount({
                scope:     'line',
                type,
                value:     numericValue,
                line_uuid: lineMatch.uuid,
            });
        }

        setValue('');
        setIsSelectorOpen(false);
    };

    return (
        <>
            <div className="dark-card flex-col" style={{ gap: 0 }}>
                <h3 className="panel-title">REMISE</h3>

                {/* Toggle % / € */}
                <div className="input-row">
                    <div className="toggle-group">
                        <button
                            className={type === 'percent' ? 'active' : ''}
                            onClick={() => setType('percent')}
                        >%</button>
                        <button
                            className={type === 'amount' ? 'active' : ''}
                            onClick={() => setType('amount')}
                        >€</button>
                    </div>
                    <input
                        ref={inputRef}
                        type="number"
                        step="0.01"
                        className="white-input flex-1"
                        placeholder={type === 'percent' ? 'ex: 10' : 'ex: 5.00'}
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleApplyCart()}
                        disabled={disabled}
                    />
                </div>

                {/* Presets rapides */}
                {type === 'percent' && (
                    <div className="presets-row" style={{ gridTemplateColumns: `repeat(${QUICK_PERCENTS.length}, 1fr)` }}>
                        {QUICK_PERCENTS.map(p => (
                            <button
                                key={p}
                                className="preset-btn"
                                onClick={() => setValue(String(p))}
                                disabled={disabled}
                            >
                                {p}%
                            </button>
                        ))}
                    </div>
                )}

                {/* Aperçu */}
                {canApply && (
                    <p className="text-muted" style={{ fontSize: 11, margin: '4px 0 8px' }}>
                        {type === 'percent' ? `${numericValue}% du total` : `${formatEUR(numericValue)} de remise`}
                    </p>
                )}

                {/* Appliquer sur */}
                <span className="apply-label">Appliquer sur :</span>
                <div className="action-row mb-section">
                    <button
                        className="pill-btn outline flex-1"
                        onClick={() => setIsSelectorOpen(true)}
                        disabled={disabled || !canApply}
                    >
                        UN ARTICLE
                        <img src={iconArrow} alt="" className="white_svg size-16 ml-auto" />
                    </button>
                    <button
                        className="pill-btn blue flex-1"
                        onClick={handleApplyCart}
                        disabled={disabled || !canApply}
                    >
                        TICKET TOTAL
                    </button>
                </div>

                {/* Avoir */}
                <h3 className="panel-title">AVOIR</h3>
                <div className="input-row mb-sm">
                    <input type="text" className="white-input flex-1" placeholder="code" disabled={disabled} />
                    <button className="scan-btn" disabled={disabled}>
                        <img src={iconScan} alt="Scanner" className="white_svg size-20" />
                    </button>
                </div>
                <button className="pill-btn blue w-full mb-sm" disabled={disabled}>
                    Encaisser un avoir
                    <img src={iconArrow} alt="" className="white_svg size-16 ml-auto" />
                </button>
                <button className="pill-btn outline w-full" disabled={disabled}>
                    LISTE DES REMISES APPLIQUÉES
                    <img src={iconArrow} alt="" className="white_svg size-16 ml-auto" />
                </button>
            </div>

            {isSelectorOpen && (
                <ItemSelector
                    items={orderItems}
                    mode="discount"
                    onApply={handleApplyItem}
                    onClose={() => setIsSelectorOpen(false)}
                />
            )}
        </>
    );
};

export default DiscountManager;